/* Copyright © Amazon.com and Affiliates: This deliverable is considered
developed Content as defined in the AWS Service Terms and the SOW between
the parties dated July 15, 2025 */

/**Module: Mobile Components (FE)
 * File: CodeScanner.tsx
 * Description: UI implementation for QR/Barcode Scanning functionality using device camera
 */

import Icon, { LeftOutlined, CloseOutlined } from "@ant-design/icons";
import { Button, Drawer, Spin } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/library";
import Webcam from "react-webcam";
import "./CodeScanner.scss";
import { LeftIcon } from "../../assets/icons/icons";

interface CodeScannerProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  scanType: "tote" | "trolley" | "location";
  onScanSuccess: (
    scannedCode: string
  ) => void | boolean | Promise<void | boolean>;
  isLoading?: boolean;
  errorMessage?: string | null;
  onErrorMessageClear?: () => void;
  keepOpenOnSuccess?: boolean;
}

const CodeScanner = ({
  open,
  setOpen,
  scanType,
  onScanSuccess,
  isLoading = false,
  errorMessage,
  onErrorMessageClear,
  keepOpenOnSuccess = false,
}: CodeScannerProps) => {
  const [isManualEntryOpen, setIsManualEntryOpen] = useState(false);
  const [manualValue, setManualValue] = useState("");
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const handleClose = () => {
    if (!isLoading) {
      setOpen(false);
      setIsScanning(false);
      setManualValue("");
      setHasSubmitted(false);
    }
  };

  const webcamRef = useRef<Webcam>(null);
  const [isScanning, setIsScanning] = useState<boolean>(false);
  const codeReader = useRef(new BrowserMultiFormatReader());

  const stopScanning = () => {
    codeReader.current.reset();
    codeReader.current.stopAsyncDecode();
    codeReader.current.stopContinuousDecode();
  };

  const handleManualEntry = () => {
    setIsManualEntryOpen(true);
    setIsScanning(false);
    setHasSubmitted(false); // Reset submission state when opening manual entry
    stopScanning();
  };

  const processScanResult = useCallback(
    async (code: string, origin: "camera" | "manual") => {
      let shouldClose = true;

      const invokeCallback = async () => {
        const result = onScanSuccess?.(code);
        if (result instanceof Promise) {
          const awaited = await result;
          if (awaited === false) {
            shouldClose = false;
          } else if (awaited === true) {
            shouldClose = true;
          }
        } else if (result === false) {
          shouldClose = false;
        }
      };

      try {
        setIsScanning(false);
        await invokeCallback();
      } finally {
        setHasSubmitted(false);
      }

      if (shouldClose && !keepOpenOnSuccess) {
        stopScanning();
        setIsManualEntryOpen(false);
        setManualValue("");
        setOpen(false);
      } else {
        if (origin === "camera") {
          setTimeout(() => {
            setIsScanning(true);
          }, 300);
        }
      }
    },
    [keepOpenOnSuccess, onScanSuccess, setOpen]
  );

  const handleManualSubmit = () => {
    if (manualValue.trim() && !isLoading) {
      setHasSubmitted(true);
      void processScanResult(manualValue.trim(), "manual");
    }
  };

  // Auto-start scanning when drawer opens
  useEffect(() => {
    if (open && !isManualEntryOpen) {
      const timer = setTimeout(() => {
        setIsScanning(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [open, isManualEntryOpen]);

  useEffect(() => {
    if (!open) {
      setIsManualEntryOpen(false);
      setManualValue("");
      setHasSubmitted(false);
      setIsScanning(false);
      stopScanning();
    }
  }, [open]);

  useEffect(() => {
    if (isScanning && webcamRef.current?.video) {
      codeReader.current.decodeFromVideoDevice(
        null,
        webcamRef.current.video,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (result: any, err: any) => {
          if (result) {
            const scannedCode = result.getText();
            console.log("Scanned:", scannedCode);

            void processScanResult(scannedCode, "camera");
          }
          if (err) {
            // Ignore NotFoundException - it's expected when no code is detected in a frame
            // Only log actual errors (not NotFoundException)
            const errorName = err?.name || err?.constructor?.name || "";
            const errorMessage = err?.message || err?.toString() || "";
            const isNotFoundException =
              errorName.includes("NotFoundException") ||
              errorMessage.includes("NotFoundException") ||
              errorMessage.includes(
                "No MultiFormat Readers were able to detect"
              );

            if (!isNotFoundException) {
              console.error("Scanner error:", err);
            }
            // Silently ignore NotFoundException - it's normal behavior when scanning
          }
        }
      );
    } else {
      stopScanning();
    }

    return () => stopScanning();
  }, [isScanning, onScanSuccess, setOpen]);

  const getScanTypeLabel = () => {
    if (scanType === "tote") return "Tote";
    if (scanType === "trolley") return "Trolley";
    return "Location";
  };

  useEffect(() => {
    if (errorMessage && onErrorMessageClear) {
      const timer = setTimeout(() => {
        onErrorMessageClear();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage, onErrorMessageClear]);

  return (
    <>
      <Drawer
        destroyOnClose
        styles={{
          body: { padding: 0 },
          content: { padding: 0 },
          mask: {},
        }}
        className="mobile-drawer scanner-drawer"
        style={{
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          padding: 0,
        }}
        height={"100vh"}
        closeIcon={null}
        title={null}
        open={open}
        onClose={handleClose}
        placement="bottom"
      >
        <div className="scanner-container">
          <div className="scanner-header">
            <Icon component={LeftIcon} onClick={handleClose} />
            <span className="scanner-title">Scan {getScanTypeLabel()} ID</span>
            <CloseOutlined
              onClick={handleClose}
              className="scanner-close-button"
              style={{
                opacity: isLoading ? 0.5 : 1,
                cursor: isLoading ? "not-allowed" : "pointer",
              }}
            />
          </div>

          <div className="scanner-content">
            {errorMessage && (
              <div className="scanner-error-banner">{errorMessage}</div>
            )}
            <div className="scanner-viewport">
              <Webcam
                videoConstraints={{ facingMode: { exact: "environment" } }}
                ref={webcamRef}
                className="webcam-view"
              />
              <div className="scanner-frame">
                <div className="scanner-line"></div>
              </div>
            </div>

            <p className="instruction-text">
              Align the QR or barcode within the frame to scan automatically
            </p>

            <Button
              onClick={handleManualEntry}
              className="manual-entry-button"
              size="large"
            >
              Try entering the ID manually
            </Button>
          </div>
        </div>
      </Drawer>

      <Drawer
        open={isManualEntryOpen}
        onClose={() => {
          if (!isLoading) {
            setIsManualEntryOpen(false);
            setManualValue("");
            setHasSubmitted(false);
          }
        }}
        className="manual-entry-drawer"
        placement="bottom"
        closable={false}
        mask={true}
        maskClosable={!isLoading}
        height="auto"
        destroyOnClose
      >
        <div className="manual-entry-content">
          <div className="manual-entry-header">
            <h2 className="manual-entry-title">Enter Code Manually</h2>
            <CloseOutlined
              className="manual-entry-close"
              onClick={() => {
                if (!isLoading) {
                  setIsManualEntryOpen(false);
                  setManualValue("");
                  setHasSubmitted(false);
                }
              }}
              style={{
                opacity: isLoading ? 0.5 : 1,
                cursor: isLoading ? "not-allowed" : "pointer",
              }}
            />
          </div>

          <div className="manual-entry-body">
            {errorMessage && (
              <div className="manual-entry-error">{errorMessage}</div>
            )}
            {isLoading && (
              <div className="manual-entry-loader-overlay">
                <Spin size="large" />
                <p className="loader-text">
                  {scanType === "trolley"
                    ? "Checking trolley..."
                    : scanType === "location"
                    ? "Checking location..."
                    : "Checking tote..."}
                </p>
              </div>
            )}
            <label className="manual-entry-label">Enter ID</label>
            <input
              type="text"
              inputMode="text"
              className="manual-entry-input"
              value={manualValue}
              onChange={(e) => setManualValue(e.target.value)}
              enterKeyHint="done"
              onKeyPress={(e) => {
                if (e.key === "Enter" && !isLoading) {
                  handleManualSubmit();
                  // Close the keyboard
                  (e.target as HTMLInputElement).blur();
                }
              }}
              autoFocus
              disabled={isLoading}
            />

            <button
              className="manual-entry-done-button"
              onClick={handleManualSubmit}
              disabled={!manualValue.trim() || isLoading}
            >
              {isLoading ? "Checking..." : "Done"}
            </button>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default CodeScanner;
